import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./IconicBlog.css";
import OurCompanies from "../Home/OurCompanies";
import { blogPosts, categoryLabels } from "./blogData";

const IconicBlog = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const categories = Object.entries(categoryLabels)
    .map(([id, name]) => ({
      id,
      name,
      count:
        id === "all"
          ? blogPosts.length
          : blogPosts.filter((post) => post.category === id).length,
    }))
    .filter((category) => category.count > 0);

  const filteredPosts =
    selectedCategory === "all"
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedCategory);

  const featuredPost = blogPosts.find((post) => post.featured);
  const regularPosts = filteredPosts.filter(
    (post) => post.id !== featuredPost?.id || selectedCategory !== "all"
  );

  const currentCategoryName =
    categories.find((category) => category.id === selectedCategory)?.name || "All Posts";

  const handleOpenPost = (slug) => {
    navigate(`/blogs/${slug}`);
  };

  return (
    <div className="bl-blog-container">
      <section className="bl-hero">
        <div className="bl-hero-content">
          <h1 className="bl-hero-title">INSIGHTS & INNOVATION</h1>
          <p className="bl-hero-subtitle">
            Exploring ideas that shape the future of business, technology, and lifestyle
          </p>
        </div>
      </section>

      {selectedCategory === "all" && featuredPost && (
        <section className="bl-featured-section">
          <div className="bl-featured-post">
            <div className="bl-featured-image-wrapper">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                className="bl-featured-image"
              />
              <div className="bl-featured-overlay" />
            </div>
            <div className="bl-featured-content">
              <span className="bl-featured-category">{featuredPost.categoryLabel}</span>
              <h2 className="bl-featured-title">{featuredPost.title}</h2>
              <p className="bl-featured-excerpt">{featuredPost.excerpt}</p>
              <div className="bl-featured-meta">
                <div className="bl-meta-author">
                  <div className="bl-author-avatar">{featuredPost.author.charAt(0)}</div>
                  <span className="bl-author-name">{featuredPost.author}</span>
                </div>
                <div className="bl-meta-details">
                  <span>{featuredPost.date}</span>
                  <span className="bl-meta-dot">&bull;</span>
                  <span>{featuredPost.readTime}</span>
                </div>
              </div>
              <button
                className="bl-read-more-btn"
                onClick={() => handleOpenPost(featuredPost.slug)}
              >
                Read Full Article
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.5 15L12.5 10L7.5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="bl-filter-section">
        <div className="bl-filter-container">
          <div className="bl-filter-header">
            <h3 className="bl-filter-title">BROWSE BY CATEGORY</h3>
          </div>

          <div className="bl-custom-dropdown-wrapper" onMouseLeave={() => setIsDropdownOpen(false)}>
            <div
              className={`bl-custom-dropdown-selected ${isDropdownOpen ? "open" : ""}`}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span>{currentCategoryName}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                style={{
                  transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                <path
                  d="M2.5 4.5L6 8L9.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {isDropdownOpen && (
              <ul className="bl-dropdown-options">
                {categories.map((category) => (
                  <li
                    key={category.id}
                    className={`bl-dropdown-item ${selectedCategory === category.id ? "active" : ""}`}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setIsDropdownOpen(false);
                    }}
                  >
                    <span className="bl-item-name">{category.name}</span>
                    <span className="bl-item-count">({category.count})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="bl-posts-section">
        <div className="bl-posts-grid">
          {regularPosts.map((post, index) => (
            <article
              key={post.id}
              className="bl-post-card"
              onClick={() => handleOpenPost(post.slug)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="bl-post-image-wrapper">
                <img src={post.image} alt={post.title} className="bl-post-image" />
                <div className="bl-post-category-badge">{post.categoryLabel}</div>
              </div>
              <div className="bl-post-content">
                <h3 className="bl-post-title">{post.title}</h3>
                <p className="bl-post-excerpt">{post.excerpt}</p>
                <div className="bl-post-footer">
                  <div className="bl-post-author">
                    <div className="bl-post-avatar">{post.author.charAt(0)}</div>
                    <div className="bl-post-author-info">
                      <span className="bl-post-author-name">{post.author}</span>
                      <div className="bl-post-meta">
                        <span>{post.date}</span>
                        <span className="bl-meta-dot">&bull;</span>
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="bl-post-read-btn"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenPost(post.slug);
                    }}
                    aria-label={`Read details for ${post.title}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M7.5 15L12.5 10L7.5 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="cl-our-company">
        <OurCompanies />
      </div>
    </div>
  );
};

export default IconicBlog;
